import { ErrorBoundary, RollbarContext, useRollbar } from '@rollbar/react'
import * as actions from 'client/actions/actionsManagement'
import { getPreviewPageData } from 'client/actions/pageActions'
import { WORK_AREA_ID } from 'client/constants/editorIds'
import { useDeviceModeContext } from 'client/context/DeviceModeContext'
import useManagement, { selectors } from 'client/hooks/useManagement'
import usePageRouteDetector from 'client/hooks/usePageRouteDetector'
import { useWebsiteSettings } from 'client/store'
import { NetworkError } from 'common/errors/NetworkError'
import { ServiceUnavailable } from 'common/errors/ServiceUnavailable'
import { useActions } from 'common/hooks/useActions'
import React, { memo, useCallback, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { Trans } from 'react-i18next'
import { fetchWebsitePreview } from 'src/editor/api/website-websites-api'
import {
  getCurrentWebsiteTheme,
  getWebsiteId,
  getWebsiteLayoutPageId,
  getWebsiteStyles,
} from 'src/editor/store/website-settings/website-settings-selector'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import { twMerge } from 'tailwind-merge'
import { PreviewNavigation } from '../preview-navigation/preview-navigation'
import {
  getVisitorCountryWithTimeout,
  prefetchVisitorCountry,
} from './visitor-country-cache'
import { WebsiteContentSkeleton } from './website-content-skeleton'

export const WebsiteContent = memo(() => {
  const contentRef = useRef<HTMLElement | null>(null)
  const rollbar = useRollbar()
  const { t } = useTypedTranslation()
  const { isLandscape, isMobile } = useDeviceModeContext()
  const isSomethingEditing = useManagement(selectors.isSomethingEditing)
  const toggleSettings = useActions(actions.toggleSettings)
  const websiteId = useWebsiteSettings(getWebsiteId)
  const websiteLayoutId = useWebsiteSettings(getWebsiteLayoutPageId)
  const websiteStyles = useWebsiteSettings(getWebsiteStyles)
  const websiteTheme = useWebsiteSettings(getCurrentWebsiteTheme)
  const { pageId: websitePageId } = usePageRouteDetector()
  const [previewHtml, setPreviewHtml] = useState<string>()
  const getPreviewData = useActions(getPreviewPageData)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    prefetchVisitorCountry()
  }, [])

  const disableMobileSettings = useCallback(
    (e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
      if ('key' in e) {
        if (e.key !== 'Enter' && e.key !== ' ') {
          return
        }
      }

      if (isMobile && isSomethingEditing && contentRef.current === e.target) {
        toggleSettings(null)
      }
    },
    [isMobile, isSomethingEditing, toggleSettings],
  )

  useEffect(() => {
    if (!websiteId || !websiteLayoutId || !websitePageId) {
      return
    }

    const fetchHtml = async () => {
      setIsLoading(true)
      try {
        const pageData = getPreviewData()
        const visitorCountry = await getVisitorCountryWithTimeout()
        const { previewHtml } = await fetchWebsitePreview({
          websiteId,
          websiteLayoutId,
          websitePageId: Number.parseInt(websitePageId, 10),
          pagePreviewData: pageData,
          visitorCountry,
          websiteSettings: {
            style: websiteStyles,
            theme: websiteTheme,
          },
        })
        setPreviewHtml(previewHtml)
      } catch (error) {
        if (error instanceof ServiceUnavailable) {
          toast.error(t('core.errors.service_temporary_unavailable'))
        } else if (error instanceof NetworkError) {
          toast.error(t('core.errors.no_connection'))
        } else {
          rollbar.error('fetchWebsitePreview error', error as Error)
        }
      } finally {
        setIsLoading(false)
      }
    }

    fetchHtml()
  }, [
    getPreviewData,
    websiteId,
    websiteLayoutId,
    websitePageId,
    websiteStyles,
    websiteTheme,
  ])

  if (isLoading) {
    return (
      <>
        <PreviewNavigation />
        <WebsiteContentSkeleton />
      </>
    )
  }

  return (
    <>
      <PreviewNavigation />
      <RollbarContext context="editor">
        <ErrorBoundary
          fallbackUI={() => (
            <Trans parent="h3">core.errors.went_wrong_contact_support</Trans>
          )}
        >
          <main
            className={twMerge(
              'relative flex h-dvh justify-center',
              isMobile && 'top-16 min-h-fit items-center',
            )}
            onClick={disableMobileSettings}
            onKeyUp={disableMobileSettings}
            ref={contentRef}
          >
            {isMobile && (
              <img
                className={twMerge(
                  'pointer-events-none absolute z-13 h-200 w-110',
                  isLandscape && 'rotate-90',
                )}
                src={`${process.env.CLOUDFRONT_SHARED}/images/preview_frame_phone.png`}
                alt="Preview Frame"
              />
            )}
            <div
              className={twMerge(
                isMobile ? 'max-h-200 max-w-96' : 'w-full',
                isLandscape && 'max-h-100 max-w-200',
              )}
            >
              <div
                id={WORK_AREA_ID}
                className={twMerge(
                  'h-[calc(100vh-var(--layout-header-height))]',
                  isMobile
                    ? 'h-190 w-96 scroll-smooth'
                    : 'flex h-full flex-col overflow-hidden',
                  isLandscape && 'h-100 w-200',
                )}
              >
                <div
                  className={twMerge(
                    'h-200 w-96',
                    isLandscape && 'h-100 w-200',
                  )}
                >
                  <iframe
                    id="editor-preview-iframe"
                    className={twMerge(
                      'no-scrollbar absolute h-full w-full',
                      isMobile && 'h-190 w-96 rounded-[51px]',
                      isLandscape && 'h-100 w-195 pl-6',
                    )}
                    title="Preview"
                    srcDoc={previewHtml}
                  />
                </div>
              </div>
            </div>
          </main>
        </ErrorBoundary>
      </RollbarContext>
    </>
  )
})
